<template>
  <div class="inventory-target-panel" v-loading="loading">
    <div class="panel-header">
      <div class="panel-title">路线半点库存需求</div>
      <div class="panel-toolbar">
        <el-date-picker v-model="bizDate" type="date" value-format="yyyy-MM-dd" placeholder="运营日" size="mini"
          style="width: 140px" :clearable="false" @change="loadRows"></el-date-picker>
        <el-select v-model="pathId" filterable clearable placeholder="选择路线" size="mini" style="width: 220px"
          @change="loadRows">
          <el-option v-for="item in pathOptions" :key="item.id" :label="item.name" :value="item.id"></el-option>
        </el-select>
        <el-button size="mini" type="primary" :loading="saving" :disabled="!bizDate || !pathId || !dirty"
          @click="saveRows">保存</el-button>
      </div>
    </div>
    <p class="panel-desc">
      按运营日、路线、半点设置库存下限（车数）。没改过显示 1，只对所选日期的求解生效。订单需要的车更多时仍按订单计。改回 1 后不再单独保存。
    </p>
    <div class="configured-dates" v-if="configuredDates.length">
      <span class="dates-label">该路线已配置：</span>
      <el-tag v-for="item in configuredDates" :key="item" size="mini" class="date-tag"
        :effect="bizDate === item ? 'dark' : 'plain'" @click="selectDate(item)">{{ item }}</el-tag>
    </div>
    <el-table v-if="pathId" :data="rows" size="mini" border class="slot-table" max-height="420">
      <el-table-column prop="time" label="时段" width="100"></el-table-column>
      <el-table-column label="库存需求" width="160">
        <template slot-scope="{ row }">
          <el-input-number v-model="row.inventoryTarget" :min="0" :step="1" :precision="0" size="mini"
            controls-position="right" @change="onTargetChange(row)"></el-input-number>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="90">
        <template slot-scope="{ row }">
          <span>{{ rowStatus(row) }}</span>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { getEvInventoryTarget, saveEvInventoryTarget } from "@/api/ev_solver";

function todayText() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

export default {
  data() {
    return {
      bizDate: todayText(),
      pathId: null,
      configuredDates: [],
      rows: [],
      baseline: {},
      loading: false,
      saving: false,
      dirty: false
    };
  },

  computed: {
    ...mapState({
      pathList: (state) => state.pathList
    }),

    pathOptions() {
      return (this.pathList || [])
        .filter((item) => item && item.id != null)
        .map((item) => ({ id: item.id, name: item.name || `路线${item.id}` }));
    }
  },

  methods: {
    selectDate(date) {
      if (!date || date === this.bizDate) {
        return;
      }
      this.bizDate = date;
      this.loadRows();
    },

    loadRows() {
      this.rows = [];
      this.baseline = {};
      this.dirty = false;
      if (!this.bizDate || this.pathId == null || this.pathId === "") {
        this.configuredDates = [];
        return;
      }
      this.loading = true;
      getEvInventoryTarget({ date: this.bizDate, pathId: this.pathId })
        .then((res) => {
          this.applyView(res.data || {});
        })
        .finally(() => {
          this.loading = false;
        });
    },

    applyView(data) {
      this.configuredDates = Array.isArray(data.configuredDates) ? data.configuredDates : [];
      const rows = Array.isArray(data.rows) ? data.rows : [];
      const baseline = {};
      this.rows = rows.map((row) => {
        const target = row.inventoryTarget == null ? 1 : row.inventoryTarget;
        baseline[row.slot] = target;
        return {
          slot: row.slot,
          time: row.time,
          inventoryTarget: target,
          configured: !!row.configured
        };
      });
      this.baseline = baseline;
      this.dirty = false;
    },

    onTargetChange(row) {
      if (row.inventoryTarget == null || row.inventoryTarget < 0) {
        row.inventoryTarget = 1;
      }
      this.refreshDirty();
    },

    refreshDirty() {
      this.dirty = this.rows.some((row) => row.inventoryTarget !== this.baseline[row.slot]);
    },

    rowStatus(row) {
      if (row.inventoryTarget !== this.baseline[row.slot]) {
        return "未保存";
      }
      return row.configured ? "已保存" : "默认";
    },

    saveRows() {
      const changed = this.rows
        .filter((row) => row.inventoryTarget !== this.baseline[row.slot])
        .map((row) => ({ slot: row.slot, inventoryTarget: row.inventoryTarget }));
      if (!changed.length) {
        return;
      }
      this.saving = true;
      saveEvInventoryTarget({ date: this.bizDate, pathId: this.pathId, rows: changed })
        .then((res) => {
          this.applyView(res.data || {});
          this.$message.success("库存需求已保存");
        })
        .finally(() => {
          this.saving = false;
        });
    }
  }
};
</script>

<style lang="less" scoped>
.inventory-target-panel {
  flex-shrink: 0;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}

.panel-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.panel-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
}

.panel-desc {
  margin: 0 0 8px;
  font-size: 12px;
  color: #909399;
}

.configured-dates {
  margin-bottom: 8px;
}

.dates-label {
  font-size: 12px;
  color: #606266;
  margin-right: 6px;
}

.date-tag {
  margin: 0 6px 4px 0;
  cursor: pointer;
}

.slot-table {
  max-width: 380px;
}
</style>
